<template>
    <div class="color-field">
        <div
            class="color-field-input"
            :class="{ 'active': isOpen }"
            @click="openPopover"
        >
            <span
                class="color-preview"
                :style="{ 'background-color': value }"
            />

            <input
                ref="hexInput"
                v-model="valueInput"
                class="color-text"
                type="text"
                :submitted="true"
                :maxlength="7"
                :pattern="hexRegex"
                @keydown.enter.prevent="select"
            />

            <Icon name="x" @click.stop.native="resetValue" />
        </div>

        <Popover
            :is-open="isOpen"
            position="bottom"
            @close="closePopover"
        >
            <div class="popover-content">
                <ColorPicker v-model="valueInput" />
            </div>
        </Popover>
    </div>
</template>

<script>
import debounce from 'lodash.debounce';

import { Popover } from '../Popover';
import { Icon } from '../Icon';
import ColorPicker from './ColorPicker';

export default {
    name: 'DsColorField',

    components: {
        ColorPicker,
        Popover,
        Icon,
    },

    props: {
        /**
         * Hex value of the color field
         */
        value: {
            type: String,
            required: true,
        },
    },

    data() {
        return {
            isOpen: false,
            hexRegex: /^#?([a-fA-F0-9]{6})$/,
            lastValue: this.value,
            delay: 300,
        };
    },

    created() {
        this.focusInput = debounce(this.focusInput, this.delay);
    },

    computed: {
        valueInput: {
            get() {
                return this.value;
            },

            set(value) {
                if (this.hexRegex.test(value)) {
                    const hexValue = value.startsWith('#') ? value : `#${value}`;

                    this.$emit('input', hexValue);
                    this.focusInput();
                }
            },
        },
    },

    methods: {
        updateValue({ hex }) {
            this.$emit('input', hex);
        },

        openPopover() {
            this.lastValue = this.value;
            this.isOpen = true;
            this.focusInput();
        },

        closePopover() {
            this.isOpen = false;
        },

        resetValue() {
            this.valueInput = this.lastValue;
        },

        focusInput() {
            this.$refs.hexInput.focus();
        },
    },
};
</script>

<style lang="scss" scoped>
    @import "../../styles/common";

    .color-field-input {
        @include transition(border-color);

        color: $color-text-normal;
        display: grid;
        grid-template-columns: $input-height px-to-rem(104px) px-to-rem(40px);
        border: $input-border;
        border-color: $input-border-color;
        border-radius: $input-border-radius;
        background: $color-paper;
        overflow: hidden;
        height: $input-height;
        width: px-to-rem(200px);

        &:hover {
            cursor: text;
        }

        > * {
            height: $input-height;
        }
    }

    .color-text {
        padding: 0 $spacing-100;
    }

    .active {
        border-color: $input-color-active;

        .icon {
            display: block;
        }
    }

    .color-preview {
        background: $color-gray-200;

        &:hover {
            cursor: pointer;
        }
    }

    input {
        font-size: $font-size-lg;
        text-transform: uppercase;
        border: 0;
    }

    .icon {
        display: none;
        width: 100%;
        padding: $spacing-100;
        --icon-color: #{$color-text-normal};

        &:hover {
            background: $color-gray-100;
            cursor: pointer;
        }
    }
</style>

<style lang="scss">
    @import "../../styles/common";

    .color-field {
        .popover-container {
            .popover {
                margin-top: 0;

                .popover-content {
                    padding: $spacing-100;
                }
            }
        }
    }
</style>
